import React, { useMemo, useState } from 'react';
import Popup from 'reactjs-popup';
import { Col, Container, Row } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye } from '@fortawesome/free-solid-svg-icons';
import Button from '../../Parts/Button';
import { GroupHeader } from '../../Parts/FormParts';
import SearchAndFilter from '../../Parts/SearchAndFilter';
import SummaryAccordionList, { type SummaryAccordionItem } from './SummaryAccordionList';
import { useQuoteAwning } from '../../Store/entities/awnings/hooks';
import { useQuoteTypeNameByQuoteId } from '../../Store/entities/quotes/hooks';
import { parseAwningMultiSelectValue } from '../awningConfiguratorUtils';

// Multi-select parameters are stored as JSON encoded arrays and read better as a joined list.
const MULTI_SELECT_PARAMETER_NAMES = ['remote', 'sensors'];

type AwningSummaryProps = {
	quoteId: number;
	lineNumber: number;
};

const formatKey = (key: string) =>
	(key === 'id' ? 'ID' : key.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase()));

const AwningSummary = ({ quoteId, lineNumber }: AwningSummaryProps) => {
	const awning = useQuoteAwning(quoteId, lineNumber);
	const quoteType = useQuoteTypeNameByQuoteId(quoteId) ?? 'default';
	const [summaryOpen, setSummaryOpen] = useState(false);
	const [searchTerm, setSearchTerm] = useState('');

	const summaryItems = useMemo<SummaryAccordionItem[]>(() => {
		if (!awning) return [];

		const items: SummaryAccordionItem[] = [];

		if (awning.msrp === 0) {
			items.push({
				eventKey: 'Awning Errors',
				title: 'Errors',
				content: [{
					key: 'Pricing',
					value: awning.error_message?.trim()
						|| 'Awning pricing failed. Unlock and update configuration to reprice.',
				}],
			});
		}

		items.push({
			eventKey: 'awning-line',
			title: 'Awning Details',
			content: [
				{ key: 'Room Name', value: awning.room_name || '-' },
				{ key: 'Shade Name', value: awning.shade_name || '-' },
				{ key: 'Part Number', value: awning.part_number || '-' },
				{ key: 'Line Number', value: awning.line_number },
			],
		});

		const configurationContent = [...(awning.parameters ?? [])]
			.sort((a, b) => a.description.localeCompare(b.description))
			.map((param) => {
				const value = MULTI_SELECT_PARAMETER_NAMES.includes(param.name)
					? parseAwningMultiSelectValue(param.value).join(', ')
					: param.value;

				return {
					key: param.description || formatKey(param.name),
					value: value || '-',
				};
			});

		items.push({
			eventKey: 'awning-configuration',
			title: 'Configuration',
			content: configurationContent,
		});

		return items;
	}, [awning]);

	if (!awning) return null;

	const displayName = awning.shade_name || awning.name || 'Awning';

	return (
		<>
			<Popup
				open={summaryOpen}
				onClose={() => setSummaryOpen(false)}
				className="mobile-modal"
			>
				<div id="detail-view" className="px-2">
					<div>
						<div className="d-flex align-items-end justify-content-start mb-4">
							<h2 className={`text-${quoteType} m-0 w-auto`}>Awning Summary</h2>
							<h3 className="text-body-tertiary my-0 mx-2 w-auto" style={{ fontSize: '18px' }}>
								{displayName}
							</h3>
						</div>
						<SearchAndFilter searchTerm={searchTerm} onSearchChange={setSearchTerm} />
						{GroupHeader('Awning Data', quoteType)}
						<SummaryAccordionList
							accordions={summaryItems}
							quoteType={quoteType}
							searchTerm={searchTerm}
						/>
						<Container className="local-bootstrap">
							<Row className="justify-content-center mt-2">
								<Col lg={4}>
									<Button color={quoteType} fullWidth onClick={() => setSummaryOpen(false)}>
										Done
									</Button>
								</Col>
							</Row>
						</Container>
					</div>
				</div>
			</Popup>
			<Button
				fullWidth
				color="light"
				size="sm"
				title="Detail View"
				hoverColor={quoteType}
				onClick={() => setSummaryOpen(true)}
			>
				<FontAwesomeIcon transform="grow-5" icon={faEye} />
			</Button>
		</>
	);
};

export default AwningSummary;
